import React, { useState } from 'react';
import { useSocket, SOCKET_STATE } from '../context/SocketContext';
import { useWebRTC } from '../context/WebRTCContext';
import { listRoles, DEFAULT_ROLE } from '../../core/roles.esm.js';
import AnimatedGemPreview from './AnimatedGemPreview';
import DesktopAppGuide from './DesktopAppGuide';
import './DeviceSelectionPrompt.css';

/**
 * DeviceSelectionPrompt
 *
 * Displayed when the user is connected but hasn't selected a device.
 * This replaces the main content area until a device is selected from
 * the gem sockets in the navbar.
 *
 * States handled:
 * 1. Has bound sessions but none selected -> "Select a device"
 * 2. Has no bound sessions -> "Pair your first device"
 * 3. Has only offline sessions -> "Waiting for device to come online"
 */

const DeviceSelectionPrompt = () => {
  const { sockets, generateCodeForSlot } = useSocket();
  const { isConnected } = useWebRTC();
  const [pairRole, setPairRole] = useState(DEFAULT_ROLE);
  const [copied, setCopied] = useState(false);
  const roleOptions = listRoles();
  const activeRole = roleOptions.find((r) => r.id === pairRole) || roleOptions[0];

  // Analyze socket states
  const boundSockets = sockets.filter(s =>
    s.state === SOCKET_STATE.BOUND_ONLINE || s.state === SOCKET_STATE.BOUND_OFFLINE
  );
  const onlineSockets = sockets.filter(s => s.state === SOCKET_STATE.BOUND_ONLINE);
  const pendingSockets = sockets.filter(s => s.state === SOCKET_STATE.PENDING);
  const emptySockets = sockets.filter(s => s.state === SOCKET_STATE.EMPTY);

  // Determine which state we're in
  const hasNoBoundDevices = boundSockets.length === 0 && pendingSockets.length === 0;
  const hasOnlyOfflineDevices = boundSockets.length > 0 && onlineSockets.length === 0;
  const hasOnlineDevices = onlineSockets.length > 0;
  const hasPendingCodes = pendingSockets.length > 0;

  // Handle generating first pairing code
  const handleGenerateCode = () => {
    const firstEmptySlot = emptySockets[0];
    if (firstEmptySlot && isConnected) {
      generateCodeForSlot(firstEmptySlot.index, pairRole);
    }
  };

  // Render different content based on state
  const renderContent = () => {
    if (hasNoBoundDevices && !hasPendingCodes) {
      // No devices at all - encourage pairing
      return (
        <div className="prompt-state pair-first">
          <div className="prompt-icon-container">
            <div className="icon-gem-empty">
              <div className="gem-socket-visual">
                <div className="socket-frame">
                  <div className="socket-inner">
                    <span className="plus-icon">+</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="icon-arrow">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="icon-gem-filled">
              <div className="gem-socket-visual filled">
                <div className="socket-frame">
                  <div className="socket-inner">
                    <AnimatedGemPreview cycleInterval={3000} size={24} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <h2>Pair Your First Device</h2>
          <p>
            Three steps and you're watching. No device connected yet — start here.
          </p>

          <DesktopAppGuide activeStep={2} className="prompt-guide" />

          <div className="prompt-role-picker">
            <label htmlFor="firstPairRole">Access level</label>
            <select
              id="firstPairRole"
              value={pairRole}
              onChange={(e) => setPairRole(e.target.value)}
            >
              {roleOptions.map((r) => (
                <option key={r.id} value={r.id}>{r.label}</option>
              ))}
            </select>
            {activeRole && <span className="prompt-role-hint">{activeRole.description}</span>}
          </div>

          <button
            className="prompt-action-btn primary"
            onClick={handleGenerateCode}
            disabled={!isConnected || emptySockets.length === 0}
          >
            <span className="btn-icon">+</span>
            Generate Pairing Code
          </button>

          <div className="prompt-hint">
            <span className="hint-icon">
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
              </svg>
            </span>
            Each gem slot can hold one paired device
          </div>
        </div>
      );
    }

    if (hasPendingCodes && boundSockets.length === 0) {
      // Has pending codes but no devices yet
      const pendingCode = pendingSockets[0];
      return (
        <div className="prompt-state waiting-claim">
          <div className="prompt-icon-container">
            <div className="waiting-indicator">
              <div className="waiting-ring" />
              <div className="waiting-ring delay-1" />
              <div className="waiting-ring delay-2" />
              <div className="waiting-core">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 6v6l4 2" strokeLinecap="round"/>
                  <circle cx="12" cy="12" r="10"/>
                </svg>
              </div>
            </div>
          </div>

          <h2>Almost there — enter this code</h2>
          <p>
            Open the desktop app on the computer you want to watch and type in the
            code below. It connects the moment the code is accepted.
          </p>

          <div className="pairing-code-display">
            <span className="code-label">Pairing Code</span>
            <div className="code-value-row">
              <span className="code-value">{pendingCode?.pairingCode}</span>
              {pendingCode?.pairingCode && (
                <button
                  type="button"
                  className="code-copy-btn"
                  onClick={() => {
                    try { navigator.clipboard?.writeText(pendingCode.pairingCode); } catch { /* clipboard unavailable */ }
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                  }}
                  aria-label="Copy pairing code"
                >
                  {copied ? 'Copied' : 'Copy'}
                </button>
              )}
            </div>
          </div>

          <DesktopAppGuide activeStep={3} className="prompt-guide" />

          <div className="prompt-hint">
            <span className="hint-icon pulse">
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6z"/>
              </svg>
            </span>
            Waiting for the desktop app to accept the code…
          </div>
        </div>
      );
    }

    if (hasOnlyOfflineDevices) {
      // Has devices but all offline - waiting for one to come online
      return (
        <div className="prompt-state devices-offline">
          <div className="prompt-icon-container">
            <div className="waiting-online-indicator">
              <div className="waiting-ring" />
              <div className="waiting-ring delay-1" />
              <div className="waiting-core offline">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 6v6l4 2" strokeLinecap="round"/>
                </svg>
              </div>
            </div>
          </div>

          <h2>No Devices Online</h2>
          <p>
            Waiting for a paired device to come online.
            Start the desktop app to connect.
          </p>

          <div className="device-list">
            {boundSockets.map(socket => (
              <div key={socket.index} className="device-item offline">
                <span className="device-gem" />
                <span className="device-name">{socket.sessionName || `Device ${socket.index + 1}`}</span>
                <span className="device-status">Offline</span>
              </div>
            ))}
          </div>

          <div className="prompt-hint">
            <span className="hint-icon">
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd"/>
              </svg>
            </span>
            Devices auto-connect when the desktop app starts
          </div>
        </div>
      );
    }

    if (hasOnlineDevices) {
      // Has online devices - prompt to select one
      return (
        <div className="prompt-state select-device">
          <div className="prompt-icon-container">
            <div className="select-indicator">
              <div className="pointer-hand">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a6 6 0 006 6h2a6 6 0 006-6v-1.5m-10-3.5V7.5a1.5 1.5 0 113 0m0 6.5V7.5m0 6.5a1.5 1.5 0 003 0M10 7.5V14" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="click-ripple" />
              <div className="click-ripple delay-1" />
            </div>
          </div>

          <h2>Select a Device</h2>
          <p>
            Click on a glowing gem above to select which device you want to control.
          </p>

          <div className="gems-preview">
            <div className="gems-row">
              {onlineSockets.map((socket, i) => (
                <div
                  key={socket.index}
                  className="gem-slot-preview"
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className="gem-glow" />
                  <div className="gem-body" />
                </div>
              ))}
            </div>
            <div className="up-arrow">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 19V5M5 12l7-7 7 7" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>

          <div className="prompt-hint">
            <span className="hint-icon">
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z"/>
              </svg>
            </span>
            {onlineSockets.length} device{onlineSockets.length !== 1 ? 's' : ''} online and ready
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="device-selection-prompt">
      <div className="prompt-container">
        {renderContent()}
      </div>

      {/* Decorative background elements */}
      <div className="prompt-bg-decoration">
        <div className="bg-grid" />
        <div className="bg-gradient" />
      </div>
    </div>
  );
};

export default DeviceSelectionPrompt;
